import React, { useState, useMemo, useRef, useEffect } from "react";
import { gymnasiumApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./Gymnasium.scss";

const EQUIPMENT_CATEGORIES = ["Cardio", "Strength", "Free Weights", "Yoga & Stretching", "Other"];
const STATUSES = ["Working", "Under Maintenance", "Broken", "Replaced"];

const BLANK_EQUIPMENT = {
  _id: null, Equipment_Name: "", Category: "Cardio", Description: "",
  Quantity: "1", Status: "Working", Date_Added: new Date().toISOString().slice(0, 10),
};

const Gymnasium = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState("");
  const [formData, setFormData] = useState(BLANK_EQUIPMENT);
  const [confirmId, setConfirmId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);

  useEffect(() => { fetchEquipment(); }, []);

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      const response = await apiClient(gymnasiumApi);
      const result = await response.json();
      if (response.ok) { setEquipment(result.data || []); }
    } catch (error) {
      console.error("Error fetching equipment:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onClose = () => setDropdownOpenId(null);
    window.addEventListener("scroll", onClose, true);
    return () => window.removeEventListener("scroll", onClose, true);
  }, []);

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 190 });
    setDropdownOpenId(id);
  };

  const openAddModal = () => { setFormData(BLANK_EQUIPMENT); setModalType("add"); setDropdownOpenId(null); };
  const openEditModal = (item) => { setFormData({ ...item }); setModalType("edit"); setDropdownOpenId(null); };
  const closeModal = () => { setModalType(""); setFormData(BLANK_EQUIPMENT); };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? gymnasiumApi : `${gymnasiumApi}/${formData._id}`;
      const response = await apiClient(url, { method, body: JSON.stringify(formData) });
      if (response.ok) { fetchEquipment(); closeModal(); }
    } catch (error) {
      console.error("Error saving equipment:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await apiClient(`${gymnasiumApi}/${id}`, { method: "DELETE" });
      fetchEquipment();
      setConfirmId(null); setDropdownOpenId(null);
    } catch (error) {
      console.error("Error deleting equipment:", error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = equipment.filter(e => 
    (e.Equipment_Name || "").toLowerCase().includes(searchText.toLowerCase()) || 
    (e.Category || "").toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="gym-page">
      <div className="gym-header">
        <div className="gym-header-left">
          <div className="gym-header-icon">🏋️</div>
          <div><h1>Gymnasium</h1><p>Manage gym equipment, inventory, and facility information.</p></div>
        </div>
        <div className="gym-header-actions">
          <button className="gym-btn-primary" onClick={openAddModal}>Add Equipment</button>
        </div>
      </div>

      <div className="gym-tabs">
        <button className={`gym-tab ${activeTab === "overview" ? "active" : ""}`} onClick={() => setActiveTab("overview")}>Overview</button>
        <button className={`gym-tab ${activeTab === "equipment" ? "active" : ""}`} onClick={() => setActiveTab("equipment")}>Equipment Inventory</button>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      {activeTab === "overview" && (
        <div className="gym-overview-tab">
          <div className="gym-preview-banner">
            <div className="gpb-left">
              <span className="gpb-badge">Modern Facility</span>
              <h2>State-of-the-art College Gym</h2>
              <p className="gpb-desc">Equipped with the latest cardio and strength training equipment. Available to all students and faculty members.</p>
              <div className="gpb-meta-row"><span>📍 Building C, Ground Floor</span><span>⏰ 6:00 AM - 9:00 PM</span></div>
            </div>
            <div className="gpb-right">
              <div className="gpb-stats-grid">
                <div><strong>{equipment.length}</strong><span>Items</span></div>
                <div><strong>{equipment.reduce((a, b) => a + (Number(b.Quantity) || 0), 0)}</strong><span>Units</span></div>
              </div>
            </div>
          </div>
          <div className="gym-section-head"><div><h2>Gym Rules & Safety</h2><p>General guidelines for all gym members.</p></div></div>
          <div className="gym-rules-box">
            <div className="gym-rule-item"><span>👟</span><div><strong>Proper Attire</strong><p>Sports shoes and gym wear are mandatory.</p></div></div>
            <div className="gym-rule-item"><span>🧼</span><div><strong>Hygiene First</strong><p>Wipe down equipment after each use.</p></div></div>
            <div className="gym-rule-item"><span>👤</span><div><strong>ID Card</strong><p>Always carry your student/staff ID.</p></div></div>
          </div>
        </div>
      )}

      {activeTab === "equipment" && (
        <div className="gym-equipment-tab">
          <div className="is-toolbar">
            <div className="is-search">🔍 <input type="text" placeholder="Search equipment..." value={searchText} onChange={e => setSearchText(e.target.value)} /></div>
          </div>
          <div className="gym-fac-grid">
            {filtered.map(item => (
              <div key={item._id} className="gym-fac-card">
                <div className="gym-fac-head">
                  <div className="gym-fac-icon">🦾</div>
                  <h3>{item.Equipment_Name}</h3>
                  <button className="is-dots" onClick={e => handleDotsClick(e, item._id)}>⋮</button>
                </div>
                <p>{item.Description}</p>
                <div className="is-card-info-row" style={{ marginTop: '10px' }}>
                  <span className={`status-badge ${item.Status.toLowerCase().replace(' ', '-')}`}>{item.Status}</span>
                  <span>Qty: {item.Quantity}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {dropdownOpenId && (
        <div ref={dropdownRef} className="is-dropdown" style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}>
          <button onClick={() => openEditModal(equipment.find(e => e._id === dropdownOpenId))}>Edit Detail</button>
          <button className="is-dd-del" onClick={() => { setConfirmId(dropdownOpenId); setDropdownOpenId(null); }}>Delete Equipment</button>
        </div>
      )}

      {(modalType === "add" || modalType === "edit") && (
        <div className="is-overlay" onClick={closeModal}>
          <div className="is-form-modal" onClick={e => e.stopPropagation()}>
            <div className="is-modal-head">
              <div><h2>{modalType === "add" ? "Add" : "Edit"} Equipment</h2><p>Update gym inventory and equipment status.</p></div>
              <button className="is-modal-close" onClick={closeModal}>×</button>
            </div>
            <form className="is-form" onSubmit={handleSave}>
              <div className="is-field full"><label>Equipment Name *</label><input value={formData.Equipment_Name} onChange={e => setFormData({...formData, Equipment_Name: e.target.value})} required /></div>
              <div className="is-field-row">
                <div className="is-field"><label>Category</label><select value={formData.Category} onChange={e => setFormData({...formData, Category: e.target.value})}>{EQUIPMENT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                <div className="is-field"><label>Quantity</label><input type="number" value={formData.Quantity} onChange={e => setFormData({...formData, Quantity: e.target.value})} /></div>
              </div>
              <div className="is-field-row">
                <div className="is-field"><label>Status</label><select value={formData.Status} onChange={e => setFormData({...formData, Status: e.target.value})}>{STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <div className="is-field"><label>Date Added</label><input type="date" value={formData.Date_Added} onChange={e => setFormData({...formData, Date_Added: e.target.value})} /></div>
              </div>
              <div className="is-field full"><label>Description</label><textarea value={formData.Description} onChange={e => setFormData({...formData, Description: e.target.value})} rows="3" /></div>
              <div className="is-form-footer"><button type="button" className="is-btn-light" onClick={closeModal}>Cancel</button><button type="submit" className="is-btn-primary">Save Equipment</button></div>
            </form>
          </div>
        </div>
      )}

      {confirmId && (
        <div className="is-overlay" onClick={() => setConfirmId(null)}>
          <div className="is-confirm" onClick={e => e.stopPropagation()}>
            <h3>Delete Equipment?</h3>
            <p>This will remove the item from gym inventory.</p>
            <div className="is-confirm-actions"><button className="is-btn-light" onClick={() => setConfirmId(null)}>Cancel</button><button className="is-btn-danger" onClick={() => handleDelete(confirmId)}>Yes, Delete</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gymnasium;